import { apiRequest } from './apiClient';
import type { PortfolioImportAccount } from './portfolioImportService';

export type SyncedPortfolioAccount = PortfolioImportAccount & {
  /** Index within the day's snapshot (for edit/delete). */
  index?: number;
};

export type SyncedPortfolioSnapshot = {
  id: string;
  asOfDate: string;
  broker: string | null;
  totalBalance: number;
  accounts: SyncedPortfolioAccount[];
  createdAt: string;
  updatedAt: string;
};

export type PortfolioSnapshotsResponse = {
  snapshots: SyncedPortfolioSnapshot[];
  latest: SyncedPortfolioSnapshot | null;
};

/** Load portfolio history from Mongo (synced across devices). */
export async function fetchPortfolioSnapshots(
  limit = 30,
  view?: 'personal' | 'combined',
): Promise<PortfolioSnapshotsResponse> {
  const params = new URLSearchParams({ limit: String(limit) });
  if (view) params.set('view', view);
  return apiRequest<PortfolioSnapshotsResponse>(
    `/portfolio/snapshots?${params.toString()}`,
  );
}

function toPayloadAccounts(accounts: SyncedPortfolioAccount[]) {
  return accounts
    .map((a) => {
      const label = (a.label ?? '').trim().slice(0, 200);
      const balance = Number(a.balance);
      if (!label || !Number.isFinite(balance) || balance < 0) return null;

      const isInvestment = a.isInvestment !== false;
      const row: {
        accountId?: string;
        label: string;
        balance: number;
        type: string;
        currency?: string;
        broker?: string | null;
        expectedReturnPct?: number;
        source?: 'import' | 'manual';
        isInvestment: boolean;
        hiddenFromHousehold?: boolean;
      } = {
        label,
        balance: Math.round(balance * 100) / 100,
        type: (a.type || (isInvestment ? 'investment' : 'other')).trim().slice(0, 64),
        isInvestment,
        hiddenFromHousehold: a.hiddenFromHousehold === true,
      };
      const accountId = a.accountId?.trim().slice(0, 64);
      if (accountId) row.accountId = accountId;
      const currency = a.currency?.trim().slice(0, 8);
      if (currency) row.currency = currency;
      if (a.broker != null && String(a.broker).trim()) {
        row.broker = String(a.broker).trim().slice(0, 80);
      }
      // Do not send ownerUserId — server stamps the caller.
      if (
        isInvestment &&
        typeof a.expectedReturnPct === 'number' &&
        Number.isFinite(a.expectedReturnPct)
      ) {
        row.expectedReturnPct = a.expectedReturnPct;
      }
      if (a.source === 'manual' || a.source === 'import') row.source = a.source;
      return row;
    })
    .filter((row): row is NonNullable<typeof row> => row != null);
}

/** Explicit save (extract endpoint also persists). Merge by default. */
export async function savePortfolioSnapshot(params: {
  asOfDate: string;
  broker?: string | null;
  accounts: SyncedPortfolioAccount[];
  replace?: boolean;
}): Promise<SyncedPortfolioSnapshot> {
  const data = await apiRequest<{ snapshot: SyncedPortfolioSnapshot }>(
    '/portfolio/snapshots',
    {
      method: 'POST',
      body: JSON.stringify({
        asOfDate: params.asOfDate,
        broker: params.broker ?? null,
        accounts: toPayloadAccounts(params.accounts),
        replace: params.replace === true,
      }),
    },
  );
  return data.snapshot;
}

/** Replace the latest (or given) day's account list after user edit/delete. */
export async function replacePortfolioAccounts(params: {
  asOfDate: string;
  broker?: string | null;
  accounts: SyncedPortfolioAccount[];
}): Promise<SyncedPortfolioSnapshot> {
  return savePortfolioSnapshot({ ...params, replace: true });
}
